import DOMPurify from "dompurify";
import qs from "querystring";
import jwtDecode from "jwt-decode";
import dayjs from "dayjs";

import * as configRequest from "./CESConfiguration";
import { FormNames } from "./CESTypes";
import CESAppInfo from "./CESAppInfo";
import currentUser from "./CESUser";

import {
  fetchDataTransformer,
  getEncodedData,
  setEncodedData,
} from "./CESUtils";

// Using let on purpose - it refreshes every page refresh and in case user changes locale
let cachedResource: any;

const apiUrl = (endpoint: string) => `${CESAppInfo.apiBaseUrl}${endpoint}`;

const basicAuthHeader = () => ({
  Authorization: `Basic ${CESAppInfo.basicAuth}`,
});

const fetchWithRetry = async (
  url: string,
  options: RequestInit,
  retries: number = 10,
  initRetryMs: number = 50,
) => {
  if (retries < 0) {
    throw new Error("retries must be a non-negative number");
  }

  let lastError: unknown;
  let lastResponse: Response | undefined;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await fetch(url, options);

      // 429 is a definitive answer carrying a translatable error body, not a
      // transient fault — retrying it only deepens the rate limit.
      if (response.status < 500) {
        return response;
      }

      lastResponse = response;
    } catch (err) {
      lastError = err;
      lastResponse = undefined;
    }

    if (attempt < retries) {
      await new Promise((resolve) => {
        setTimeout(resolve, Math.random() * initRetryMs * Math.pow(2, attempt));
      });
    }
  }

  // Hand the exhausted response back so fetchDataTransformer can throw the
  // structured { status, data } the error handlers expect. Only a genuine
  // network failure has no response to surface.
  if (lastResponse) {
    return lastResponse;
  }

  throw lastError;
};

const getAccessTokenBase = async () => {
  const token = currentUser.getRefreshToken();
  const config = configRequest.accessTokenConfig();

  const response = await fetch(apiUrl(config.endpoint), {
    method: config.method,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = fetchDataTransformer(response);
  return (await data).data.token;
}

export const getAccessToken = async () => {
  let accessToken = currentUser.getAccessToken();

  if (accessToken) {
    const tokenClaims: any = jwtDecode(accessToken);

    const remaining = tokenClaims.exp - dayjs().unix() - 60;
    if (remaining <= 0) {
      try {
        const token = await getAccessTokenBase();
        currentUser.setAccessToken(token);
        accessToken = token;
      } catch (_e) { 
        console.error("Failed to refresh access token, clearing user session.", _e);
        currentUser.clear();
        accessToken = null;
       }
    }
  }

  return accessToken;
}

export const getRegionsList = () => {
  const clientId = CESAppInfo.ciamClientId;
  const key = `${clientId}-api-config`;
  const data = getEncodedData(key);

  if (data) {
    return Promise.resolve({ data });
  }

  return fetch(`${CESAppInfo.cdnBaseUrl}/${clientId}.json`)
    .then((response) => {
      if (!response.ok) {
        console.warn(`[CESRequest] Failed to load client settings (${response.status}). Using defaults.`);
        return { status: response.status, data: [] };
      }
      return fetchDataTransformer(response);
    })
    .then((res: any) => {
      if (res.data) {
        setEncodedData(key, res.data);
      }
      return res;
    })
    .catch((err) => {
      console.warn('[CESRequest] Network error loading client settings. Using defaults.', err);
      return { data: [] };
    });
};

export const getBundleResource = async (bundleIdentifier: string) => {
  const resource: string = cachedResource[bundleIdentifier];

  if (resource) return DOMPurify.sanitize(resource, { ADD_ATTR: ["target"] });

  if (
    bundleIdentifier.indexOf("masks.") === -1
    && bundleIdentifier.indexOf(".listRef") === -1
    && bundleIdentifier.indexOf(".placeholder") === -1
  ) {
    console.warn("Missing bundleIdentifier: ", bundleIdentifier);
  }

  throw cachedResource["resource.does.not.exist"];
};

export const getBundleResourceTranslation = (bundleIdentifier: string, warnMissing: boolean = true) => {
  const resource: string = cachedResource[bundleIdentifier];

  if (resource) return DOMPurify.sanitize(resource, { ADD_ATTR: ["target"] });

  if (
    bundleIdentifier.indexOf("masks.") === -1
    || bundleIdentifier.indexOf(".listRef") === -1
    || bundleIdentifier.indexOf(".placeholder") === -1
  ) {
    if (warnMissing) {
      console.warn("Missing bundleIdentifier: ", bundleIdentifier);
    }
  }

  throw cachedResource["resource.does.not.exist"];
};

/**
 * Get the resource bundle and add this bundle to a cached variable
 */
export const getBundle = () => {
  const { bundlePath, cdnBaseUrl } = CESAppInfo;

  return new Promise((resolve) => {
    if (!cachedResource) {
      if (!bundlePath) {
        resolve(null);
        return;
      }

      const key = `${CESAppInfo.ciamClientId}-${bundlePath}`;
      const bundleData = getEncodedData(key);
      if (bundleData) {
        cachedResource = bundleData;
        resolve(cachedResource);
      } else if (cdnBaseUrl && bundlePath) {
        // Join with exactly one slash regardless of whether cdnBaseUrl has a
        // trailing slash or bundlePath has a leading one.
        const bundleUrl = `${cdnBaseUrl.replace(/\/+$/, "")}/${bundlePath.replace(/^\/+/, "")}`;
        fetch(bundleUrl)
          .then(fetchDataTransformer)
          .then((jsonObject: any) => {
            setEncodedData(key, jsonObject.data);
            cachedResource = jsonObject.data;
            resolve(cachedResource);
          })
          .catch(() => {
            // Don't cache transient failures — leave cachedResource unset so a
            // later call can retry. Resolve empty so init() degrades gracefully
            // (form labels missing) rather than rejecting.
            console.warn(`[SDK] Translation bundle not found: ${bundleUrl}. Form labels will be missing.`);
            resolve({});
          });
      } else {
        resolve(null);
      }
    } else {
      resolve(cachedResource);
    }
  });
};

/**
 * Get the fields of the form.
 */
export const getProfileFields = async (clientId: string) => {
  const query = qs.stringify({ clientId });
  const config = configRequest.getProfileFormConfig(query);
  const token = await getAccessToken();

  if (!token) {
    return Promise.reject("Unauthorized");
  }

  return fetchWithRetry(apiUrl(config.endpoint), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then(fetchDataTransformer)
    .catch((error) => {
      verifyResponseStatus(error);
      return Promise.reject(error);
    });
};

export const verifyResponseStatus = (response: any) => {
  if (response && response.status && response.status === 401) {
    currentUser.clear();
  }
};

/**
 * Get the fields of the form.
 */
export const getFields = (formName: string, clientId: string, otp?: string) => {
  const query = qs.stringify({ formName, clientId, ...(otp && { otp }) });
  const config = configRequest.getFormConfig(query);
  
  return fetchWithRetry(apiUrl(config.endpoint), {
    headers: {
      "Content-Type": "application/json",
      ...basicAuthHeader(),
    },
  })
    .then(fetchDataTransformer)
    .catch((error) => {
      verifyResponseStatus(error);
      return error;
    });
};

/**
 * Submit form to CES API.
 */
export const submitForm = (json: string, formType: string, formValues?: any) => new Promise((resolve, reject) => {
  const forms = {
    ...JSON.parse(json),
    experienceName: CESAppInfo.experienceName,
    clientId: CESAppInfo.ciamClientId,
    ...formValues,
  };

  const body = JSON.stringify(forms);
  const config = configRequest.submitFormConfig(body, formType);

  return fetchWithRetry(apiUrl(config.endpoint), {
    method: config.method,
    headers: {
      "Content-Type": "application/json",
      ...basicAuthHeader(),
    },
    body,
  }, 0)
    .then(fetchDataTransformer)
    .then((response) => {
      resolve(response);
    })
    .catch((error) => {
      reject(error);
    });
});

/**
 * Submit form to CES API.
 */
export const submitConsumerForm = (json: string, formType: FormNames) => {
  const forms = JSON.parse(json);
  forms.experienceName = CESAppInfo.experienceName;
  forms.clientId = CESAppInfo.ciamClientId;
  const body = JSON.stringify(forms);

  const config = configRequest.submitFormConfig(body, formType);

  return fetchWithRetry(apiUrl(config.endpoint), {
    method: config.method,
    headers: {
      "Content-Type": "application/json",
      ...basicAuthHeader(),
    },
    body,
  })
    .then(fetchDataTransformer)
    .catch(
      (error) => new Promise((_, reject) => {
        verifyResponseStatus(error);
        reject(error);
      })
    );
};

/**
 * Submit authenticated form to CES API.
 */
export const submitAuthForm = (json: string, formType: FormNames) => {
  const uuid = currentUser.getUserId();
  const clientId = CESAppInfo.ciamClientId;

  return new Promise((resolve, reject) => {
    if (!uuid || !clientId) {
      reject({
        status: "3",
        message: "Unauthorized.",
      });
      return;
    } else {
      const doRequest = async () => {
        const token = await getAccessToken();
        if (!token) {
          reject({
            status: "3",
            message: "Unauthorized.",
          });
        }
        const forms = JSON.parse(json);
        forms.experienceName = CESAppInfo.experienceName;
        const body = JSON.stringify(forms);
        const config = configRequest.submitFormConfig(body, formType);

        return fetch(`${apiUrl(config.endpoint)}?clientId=${clientId}`, {
          method: config.method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body,
        }).then(fetchDataTransformer);
      };

      return doRequest()
        .then(resolve)
        .catch((error) => {
          verifyResponseStatus(error);
          reject(error);
        });
    }
  });
};

/**
 * Calls API to get user info
 */
export const getUserInfo = () => {
  const clientId = CESAppInfo.ciamClientId;
  const config = configRequest.getUserInfoConfig();

  const doRequest = async () => {
    const token = await getAccessToken();
    if (!token) {
      return Promise.reject("Unauthorized");
    }

    return fetchWithRetry(`${apiUrl(config.endpoint)}?clientId=${clientId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(fetchDataTransformer)
      .catch((error) => {
        verifyResponseStatus(error);
        return Promise.reject(error);
      });
  };

  return doRequest();
};

/**
 * Calls API to check email verification
 */
export const checkVerification = (code: string) => {
  const config = configRequest.checkEmailVerificaiton(code);

  return fetchWithRetry(apiUrl(config.endpoint), {
    method: config.method,
    headers: {
      "Content-Type": "application/json",
      ...basicAuthHeader(),
    },
    body: config.body,
  })
    .then(fetchDataTransformer)
    .then((response) => Promise.resolve(response?.data))
    .catch((error) => {
      verifyResponseStatus(error);
      return Promise.reject(error);
    });
};

/**
 * Request the logout action in frontEnd.
 */
export const logOut = async () => {
  try {
    const token = await getAccessToken();
    const config = configRequest.logoutConfig();

    await fetch(apiUrl(config.endpoint), {
      method: config.method,
      headers: { Authorization: `Bearer ${token}` },
    });
  } finally {
    const clientKey = CESAppInfo.ciamClientId;
    sessionStorage.removeItem(`${clientKey}-api-config`);
    if (CESAppInfo.bundlePath) {
      sessionStorage.removeItem(`${clientKey}-${CESAppInfo.bundlePath}`);
    }

    currentUser.clear();
  }
}

export const putRequest = (url: string, config: any) => {
  const clientId = CESAppInfo.ciamClientId;

  return new Promise((resolve, reject) => {
    if (!clientId) {
      reject({
        status: "3",
        message: "Unauthorized."
      });
      return;
    } else {
      const doRequest = async () => {
        const token = await getAccessToken();
        if (!token) {
          // Without the return the request went out as "Bearer null".
          reject({
            status: "3",
            message: "Unauthorized."
          });
          return undefined;
        }

        return fetchWithRetry(`${url}?clientId=${clientId}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: config.body
        })
          .then(fetchDataTransformer)
          .then((response) => {
            resolve(response);
          }).catch((error) => {
            verifyResponseStatus(error);
            reject(error);
          });
      };

      return doRequest().catch((error) => reject(error));
    }
  });
}
